import { useState, useCallback } from 'react';

export const useImageUpload = (options = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    maxFiles = 10,
  } = options;

  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Validate file
  const validateFile = useCallback((file) => {
    const errors = [];

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      errors.push(`File ${file.name}: Format tidak didukung. Gunakan ${allowedTypes.join(', ')}`);
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / 1024 / 1024;
      errors.push(`File ${file.name}: Ukuran terlalu besar. Maksimal ${maxSizeMB}MB`);
    }

    return errors;
  }, [allowedTypes, maxSize]);

  // Create preview URL
  const createPreview = useCallback((file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve({
          file,
          preview: e.target.result,
          name: file.name,
          size: file.size,
          id: Date.now() + '-' + Math.random().toString(36).substr(2, 9),
        });
      };
      reader.readAsDataURL(file);
    });
  }, []);

  // Add images
  const addImages = useCallback(async (files) => {
    const fileArray = Array.from(files);
    const newErrors = [];

    // Check max files limit
    if (images.length + fileArray.length > maxFiles) {
      newErrors.push(`Maksimal ${maxFiles} file yang dapat diupload`);
      setErrors(newErrors);
      return;
    }

    // Validate and process files
    const validFiles = [];
    for (const file of fileArray) {
      const fileErrors = validateFile(file);
      if (fileErrors.length === 0) {
        validFiles.push(file);
      } else {
        newErrors.push(...fileErrors);
      }
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
    }

    if (validFiles.length > 0) {
      setUploading(true);
      
      try {
        // Create previews for valid files
        const newPreviews = await Promise.all(
          validFiles.map(file => createPreview(file))
        );

        setImages(prev => [...prev, ...validFiles]);
        setPreviews(prev => [...prev, ...newPreviews]);
        setErrors([]);
      } catch (error) {
        setErrors(['Error creating image previews']);
      } finally {
        setUploading(false);
      }
    }
  }, [images.length, maxFiles, validateFile, createPreview]);

  // Remove image
  const removeImage = useCallback((index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => {
      // Revoke object URL to prevent memory leaks
      const preview = prev[index];
      if (preview && preview.preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
    setErrors([]);
  }, []);

  // Remove image by ID
  const removeImageById = useCallback((id) => {
    const index = previews.findIndex(preview => preview.id === id);
    if (index !== -1) {
      removeImage(index);
    }
  }, [previews, removeImage]);

  // Clear all images
  const clearImages = useCallback(() => {
    // Revoke all object URLs
    previews.forEach(preview => {
      if (preview.preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview.preview);
      }
    });

    setImages([]);
    setPreviews([]);
    setErrors([]);
  }, [previews]);

  // Move image position
  const moveImage = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    setImages(prev => {
      const newImages = [...prev];
      const [movedImage] = newImages.splice(fromIndex, 1);
      newImages.splice(toIndex, 0, movedImage);
      return newImages;
    });

    setPreviews(prev => {
      const newPreviews = [...prev];
      const [movedPreview] = newPreviews.splice(fromIndex, 1);
      newPreviews.splice(toIndex, 0, movedPreview);
      return newPreviews;
    });
  }, []);

  // Set existing images (for edit mode)
  const setExistingImages = useCallback((existingImages) => {
    const processedImages = existingImages.map((img, index) => ({
      ...img,
      id: img.id || `existing-${index}`,
      preview: img.url || img.preview,
      isExisting: true,
    }));

    setPreviews(processedImages);
    setImages([]);
  }, []);

  return {
    images,
    previews,
    errors,
    uploading,
    addImages,
    removeImage,
    removeImageById,
    clearImages,
    moveImage,
    setExistingImages,
    hasImages: images.length > 0 || previews.length > 0,
    imageCount: Math.max(images.length, previews.length),
  };
};